"use client"

import { useEffect, useState } from "react"

const TableOfContents= ({
  contentRef = null,
  title = "Table of Contents",
}) => {
  const [sections, setSections] = useState([])
  const [expandedSections, setExpandedSections] = useState({})
  const [activeId, setActiveId] = useState(null)

  useEffect(() => {
    const container = contentRef?.current || document

    const headings = Array.from(
      container.querySelectorAll("h2, h3, h4, h5, h6")
    )

    if (!headings.length) {
      setSections([])
      return
    }

    const usedIds = new Set()

    // Generate unique IDs where needed.
    headings.forEach((heading, index) => {
      if (!heading.id) {
        const baseId = `heading-${index}`
        let id = baseId
        let suffix = 1

        while (
          document.getElementById(id) ||
          usedIds.has(id)
        ) {
          id = `${baseId}-${suffix++}`
        }

        heading.id = id
      }

      usedIds.add(heading.id)
    })

    // Build the TOC hierarchy.
    const result = []
    let currentSection = null

    headings.forEach((heading) => {
      const level = Number(heading.tagName.substring(1))

      const item = {
        id: heading.id,
        text: heading.textContent?.trim() || "Untitled section",
        level,
      }

      if (level === 2) {
        currentSection = {
          ...item,
          children: [],
        }

        result.push(currentSection)
      } else if (currentSection) {
        currentSection.children.push(item)
      }
    })

    setSections(result)

    // Expand sections by default.
    setExpandedSections(
      Object.fromEntries(
        result.map((section) => [section.id, true])
      )
    )

    // Highlight the current heading while scrolling.
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleHeadings = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top -
              b.boundingClientRect.top
          )

        if (!visibleHeadings.length) return

        const heading = visibleHeadings[0].target
        setActiveId(heading.id)

        // Automatically expand the parent H2.
        const parentSection = result.find(
          (section) =>
            section.id === heading.id ||
            section.children.some(
              (child) => child.id === heading.id
            )
        )

        if (parentSection) {
          setExpandedSections((previous) => ({
            ...previous,
            [parentSection.id]: true,
          }))
        }
      },
      {
        rootMargin: "-40% 0px -55% 0px",
        threshold: 0,
      }
    )

    headings.forEach((heading) => observer.observe(heading))

    return () => observer.disconnect()
  }, [contentRef])

  const toggleSection = (id) => {
    setExpandedSections((previous) => ({
      ...previous,
      [id]: !previous[id],
    }))
  }

  if (!sections.length) return null

  return (
    <nav
      aria-label={title}
      className="mb-8 rounded-lg bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100 opacity-75"
    >
      <strong className="mb-3 block text-base font-semibold">
        {title}
      </strong>

      <ul className="m-0 list-none space-y-2 p-0">
        {sections.map((section) => {
          const isExpanded = !!expandedSections[section.id]
          const hasChildren = section.children.length > 0

          return (
            <li key={section.id}>
              <div className="flex items-center gap-1">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${section.text}`}
                    className={`flex h-[22px] w-[18px] shrink-0 items-center justify-center border-0 bg-transparent p-0 text-xl leading-none transition-transform duration-150 ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                  >
                    ❯
                  </button>
                ) : (
                  <span className="w-[18px] shrink-0" />
                )}

                <a
                  href={`#${section.id}`}
                  onClick={() => setActiveId(section.id)}
                  className={`text-sm transition-colors duration-200 hover:underline ${
                    activeId === section.id
                      ? "text-[#5FEAD4]"
                      : "text-inherit"
                  }`}
                >
                  {section.text}
                </a>
              </div>

              {hasChildren && isExpanded && (
                <ul className="ml-8 mt-2 list-none space-y-2 p-0 text-sm">
                  {section.children.map((child) => (
                    <li
                      key={child.id}
                      style={{
                        marginLeft: `${(child.level - 3) * 20}px`,
                      }}
                    >
                      <a
                        href={`#${child.id}`}
                        onClick={() => setActiveId(child.id)}
                        className={`transition-colors duration-200 hover:underline ${
                          activeId === child.id
                            ? "text-[#5FEAD4]"
                            : "text-inherit"
                        }`}
                      >
                        {child.text}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default TableOfContents